import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles, siteInfo } from '@/mocks/siteData';

const articleDetailImages: Record<string, string> = {
  'kaiyun-register-guide': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20registration%20form%20illustration%20with%20clean%20warm%20lighting%2C%20flat%20design%20style%2C%20soft%20cream%20background%2C%20signup%20concept%20with%20subtle%20geometric%20shapes%2C%20editorial%20quality%2C%20professional%20aesthetic&width=1200&height=500&seq=detail-register&orientation=landscape',
  'kaiyun-login-guide': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20login%20screen%20illustration%20with%20clean%20warm%20lighting%2C%20flat%20design%20style%2C%20soft%20cream%20background%2C%20authentication%20concept%20with%20subtle%20geometric%20shapes%2C%20editorial%20quality%2C%20professional%20aesthetic&width=1200&height=500&seq=detail-login&orientation=landscape',
  'kaiyun-security-guide': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20cybersecurity%20illustration%20with%20clean%20warm%20lighting%2C%20flat%20design%20style%2C%20soft%20cream%20background%2C%20lock%20shield%20concept%20with%20subtle%20geometric%20shapes%2C%20editorial%20quality%2C%20professional%20aesthetic&width=1200&height=500&seq=detail-security&orientation=landscape',
  'kaiyun-announcement-2026': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20announcement%20megaphone%20illustration%20with%20clean%20warm%20lighting%2C%20flat%20design%20style%2C%20soft%20cream%20background%2C%20update%20concept%20with%20subtle%20geometric%20shapes%2C%20editorial%20quality%2C%20professional%20aesthetic&width=1200&height=500&seq=detail-announce&orientation=landscape',
  'kaiyun-premier-league-guide': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20football%20soccer%20stadium%20illustration%20with%20clean%20warm%20lighting%2C%20flat%20design%20style%2C%20soft%20cream%20background%2C%20sports%20concept%20with%20subtle%20geometric%20shapes%2C%20editorial%20quality%2C%20professional%20aesthetic&width=1200&height=500&seq=detail-premier&orientation=landscape',
  'kaiyun-password-reset': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20password%20reset%20illustration%20with%20clean%20warm%20lighting%2C%20flat%20design%20style%2C%20soft%20cream%20background%2C%20key%20concept%20with%20subtle%20geometric%20shapes%2C%20editorial%20quality%2C%20professional%20aesthetic&width=1200&height=500&seq=detail-pwreset&orientation=landscape',
};

function renderContent(content: string) {
  const lines = content.trim().split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mt-10 mb-4">
          {line.replace('## ', '')}
        </h2>
      );
      i++;
    } else if (line.startsWith('### ')) {
      elements.push(
        <h3 key={i} className="text-lg md:text-xl font-semibold font-heading text-foreground-900 mt-8 mb-3">
          {line.replace('### ', '')}
        </h3>
      );
      i++;
    } else if (line.startsWith('**') && line.endsWith('**')) {
      const clean = line.replace(/^\*\*/, '').replace(/\*\*$/, '');
      elements.push(
        <p key={i} className="text-sm md:text-base font-semibold text-foreground-900 mt-6 mb-2">
          {clean}
        </p>
      );
      i++;
    } else if (line.match(/^\d+\.\s/)) {
      elements.push(
        <li key={i} className="text-sm md:text-base text-foreground-700 ml-5 list-decimal leading-relaxed">
          {line.replace(/^\d+\.\s/, '')}
        </li>
      );
      i++;
    } else if (line.startsWith('- ')) {
      elements.push(
        <li key={i} className="text-sm md:text-base text-foreground-700 ml-5 list-disc leading-relaxed">
          {line.replace('- ', '')}
        </li>
      );
      i++;
    } else if (line.trim() === '') {
      elements.push(<br key={i} />);
      i++;
    } else if (line.startsWith('**Q:')) {
      const qMatch = line.match(/\*\*Q:\s(.+?)\*\*/);
      const question = qMatch ? qMatch[1] : line.replace(/\*\*/g, '');
      elements.push(
        <p key={i} className="text-sm md:text-base font-semibold text-foreground-900 mt-6 mb-2">
          <strong>Q: {question}</strong>
        </p>
      );
      i++;
    } else if (line.startsWith('**A:')) {
      const aMatch = line.match(/\*\*A:\s(.+?)\*\*/);
      const answer = aMatch ? aMatch[1] : line.replace(/\*\*/g, '');
      elements.push(
        <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed pl-4 border-l-2 border-primary-200">
          <strong>A: </strong>{answer}
        </p>
      );
      i++;
    } else {
      elements.push(
        <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">
          {line}
        </p>
      );
      i++;
    }
  }

  return elements;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <section className="py-20 px-4 md:px-6 text-center">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200/50 text-foreground-400 mb-4">
          <i className="ri-file-unknow-line text-2xl"></i>
        </div>
        <h1 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
        <p className="text-foreground-500 text-sm mb-6">该文章不存在或已被移除</p>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </section>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: { '@type': 'Organization', name: 'kaiyun.com' },
            publisher: { '@type': 'Organization', name: 'kaiyun.com', url: siteInfo.url },
            url: `${siteInfo.url}/article/${article.slug}`,
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${siteInfo.url}/` },
              { '@type': 'ListItem', position: 2, name: article.categoryName, item: `${siteInfo.url}/category/${article.category}` },
              { '@type': 'ListItem', position: 3, name: article.title, item: `${siteInfo.url}/article/${article.slug}` },
            ],
          }),
        }}
      />

      <article className="py-10 md:py-14 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">{article.categoryName}</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
          </nav>

          <div className="rounded-xl overflow-hidden mb-8">
            <img
              src={articleDetailImages[article.slug]}
              alt={article.title}
              className="w-full h-56 md:h-80 object-cover"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            <Link
              to={`/category/${article.category}`}
              className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              {article.categoryName}
            </Link>
            <span className="text-xs text-foreground-400">{article.date}</span>
            <span className="text-xs text-foreground-400 flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
          </div>

          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 leading-tight mb-8">
            {article.title}
          </h1>

          <div className="prose-custom">
            {renderContent(article.content)}
          </div>

          <div className="mt-10 pt-8 border-t border-background-200/60">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-12 pt-8 border-t border-background-200/60">
              <h3 className="text-lg font-semibold font-heading text-foreground-900 mb-5">
                相关文章
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group p-4 rounded-lg bg-background-100 border border-background-200/60 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {ra.title}
                    </h4>
                    <p className="mt-1 text-xs text-foreground-400">{ra.date}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </>
  );
}